import withRouter from "components/Common/withRouter";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import {
    Card,
    CardBody,
    Col,
    Container,
    Form,
    FormFeedback,
    Input,
    Label,
    Row,
} from "reactstrap";

import ToastDialog from "components/Common/ToastDialog";
import { dateFormatChange, uId, isPermission } from "common/utility";
import API_SERVICES from "constants/app.constants";
import {
    addAuthorisedPerson,
    editEntity,
    getAuthorisedPersonList,
    getOneEntity,
} from "http/fundLync/entityAndAddress.helper";
import { callGetGSTINDetails } from "http/gstin/gstin";
import moment from "moment/moment";
import { useAsyncDebounce } from "react-table";
import { callGetCINDetails } from "../../http/cin/cin";
import { formatDateString } from "../EntityDashboard/borrowingProfile";
import CommonDialog from "components/Common/CommonDialog";
import { callFinancialCINSubmit } from "http/fundLync/entityDocuments.helper";
import Breadcrumbs from 'components/Common/BreadcrumbTitle';
import { useNavigate } from "react-router-dom";
import PartnerAddressDetails from "./addressDetails";
import PartnerUserDetails from "./userDetails";

const UpdatePartner = (props) => {
    document.title = "Partner | FundLync";

    const currentDate = new Date().toISOString().split("T")[0];
    const urlParams = new URLSearchParams(window.location.search);
    const encodedId = urlParams.get("id");
    const entityId = atob(encodedId);
    const [showCommonDialog, setShowCommonDialog] = useState({ isShow: false });
    const [isGSTINSerching, setGSTINSerching] = useState(false);
    const [gstinInfo, setGstinInfo] = useState();
    const [entity, setEntity] = useState([]);
    const [toastInfo, setToastInfo] = useState({ showToast: false });
    const [isConstitution, setConstitution] = useState(false);
    const [isEditEntityPermission, setEditEntityPermission] = useState(false);
    const navigate = useNavigate()

    const onGSTINChange = useAsyncDebounce((value) => {
        if (!validation.errors.gstnIn) findGSTINDetails(value);
    }, 400);

    const checkPermissions = async () => {
        const editEntitypermission = await isPermission(
            "entity_detail",
            "edit_permission"
        );
        setEditEntityPermission(editEntitypermission);
    };

    useEffect(() => {
        getOneEntity({ entityId })
            .then((response) => {
                let entityData = response.data;
                const formatedDate = formatDateString(
                    entityData[0].dateOfIncorporation
                );
                if (
                    entityData[0].entityConstitution == "Private Limited Company" ||
                    entityData[0].entityConstitution == "Public Limited Company"
                ) {
                    setConstitution(true);
                } else {
                    setConstitution(false);
                }
                entityData[0].dateOfIncorporation = formatedDate;
                setEntity(entityData);
            })
            .catch((error) => {
                console.log(error);
            });
        checkPermissions();
    }, []);

    const findGSTINDetails = (gstin) => {
        setGSTINSerching(true);
        callGetGSTINDetails({ gstnIn: gstin })
            .then((result) => {
                if (result.code == 200 && result.status) {
                    const { data: gstinDetails } = result;
                    const { ctb, rgdt } = gstinDetails;
                    setGstinInfo(gstinDetails);

                    validation.setFieldValue("entityConstitution", ctb);
                    if (
                        ctb == "Private Limited Company" ||
                        ctb == "Public Limited Company"
                    ) {
                        setConstitution(true);
                    } else {
                        setConstitution(false);
                    }
                    validation.setFieldValue(
                        "dateOfIncorporation",
                        dateFormatChange(rgdt, "DD/MM/YYYY", "YYYY-MM-DD")
                    );
                } else {
                    setToastInfo({
                        message: "Incorrect GSTIN",
                        showToast: true,
                        role: "error",
                    });
                    validation.setFieldValue("gstnIn", "");
                    validation.setFieldValue("entityConstitution", "");
                    validation.setFieldValue("dateOfIncorporation", "");
                }
            })
            .catch((e) => {
                setToastInfo({
                    message: "GSTIN not found",
                    showToast: true,
                    role: "error",
                });
                validation.setFieldValue("entityConstitution", "");
                validation.setFieldValue("dateOfIncorporation", "");
            })
            .finally((f) => {
                setGSTINSerching(false);
            });
    };

    const validation = useFormik({
        enableReinitialize: true,

        initialValues: {
            entityName: entity[0]?.entityName || "",
            gstnIn: entity[0]?.gstnIn || "",
            cin: entity[0]?.cin || "",
            pan: entity[0]?.pan || "",
            entityConstitution: entity[0]?.entityConstitution || "",
            entityType: entity[0]?.entityType || "",
            dateOfIncorporation: entity[0]?.dateOfIncorporation || "",
            companyDescription: entity[0]?.companyDescription || "",
        },
        validationSchema: Yup.object({
            entityName: Yup.string().required("Please Enter Entity Name"),
            gstnIn: Yup.string()
                .test("gstnCheck", "Enter correct GSTIN!", (value) => {
                    if (value && value.length > 0) {
                        const isGstinValid = Yup.string()
                            .matches(API_SERVICES.GSTN_PATTERN, "Enter correct GSTIN!")
                            .isValidSync(value);

                        return isGstinValid;
                    }

                    return true;
                })
                .test("panCheck", "GSTIN & PAN mismatch", (value, { parent }) => {
                    if (value?.length > 0) {
                        const pan = value.substring(2, 12);
                        return pan === parent.pan || !parent.pan;
                    }

                    return true;
                }),
            cin: isConstitution
                ? Yup.string()
                    .matches(API_SERVICES.CIN_PATTERN, "Enter a valid CIN number")
                    .required("Please Enter CIN")
                : Yup.string().matches(
                    API_SERVICES.CIN_PATTERN,
                    "Enter a valid CIN number"
                ),
            pan: Yup.string()
                .matches(API_SERVICES.PAN_PATTERN, "Enter a valid PAN number")
                .required("PAN is required"),
            entityConstitution: Yup.string().required(
                "Please Enter Entity Constitution"
            ),
            dateOfIncorporation: Yup.string().required(
                "Please Enter Date of Incorporation"
            )

        }),
        onSubmit: (values) => {
            const entityObj = {
                entityName: values.entityName,
                entityType: values.entityType,
                gstnIn: values.gstnIn,
                cin: values.cin,
                pan: values.pan,
                entityConstitution: values.entityConstitution,
                dateOfIncorporation: values.dateOfIncorporation,
                companyDescription: values.companyDescription,
                id: +entityId,
            };

            if (gstinInfo) {
                const { adadr, pradr } = gstinInfo;
                const isAddressExists =
                    pradr.hasOwnProperty("addr") || adadr.length > 0;

                if (isAddressExists) {
                    const { addr: address } = pradr.hasOwnProperty("addr")
                        ? pradr
                        : adadr[0];
                    const addressStr = [address.bno, address.st, address.loc].join(",");

                    if (address) {
                        entityObj["address"] = {
                            address1: addressStr,
                            city: address.dst,
                            state: address.stcd,
                            pincode: address.pncd,
                            entityId: +entityId,
                            addressType: "gstn",
                        };
                    }
                }
            }
            editEntity(entityObj)
                .then((response) => {
                    if (response.code == 201 && response.status) {
                        if (
                            (entityObj.entityConstitution == "Private Limited Company" ||
                                entityObj.entityConstitution == "Public Limited Company") &&
                            entityObj.cin &&
                            entity[0].status == 3
                        ) {
                            callAddDirectorFromCIN(entityObj.cin);
                            callAddShareholdingFromCIN(entityObj.cin);
                        }
                        setToastInfo({
                            message: "Entity Details Saved",
                            showToast: true,
                            role: "success",
                        });
                    } else {
                        const { message } = response;
                        setToastInfo({
                            message: message,
                            showToast: true,
                            role: "error",
                        });
                    }
                    getOneEntity({ entityId })
                        .then((response) => {
                            let entityData = response.data;
                            const formatedDate = formatDateString(
                                entityData[0].dateOfIncorporation
                            );
                            entityData[0].dateOfIncorporation = formatedDate;
                            setEntity(entityData);
                        })
                        .catch((error) => {
                            console.log(error);
                        });
                })
                .catch((error) => {
                    console.log(error);
                });
            validation.resetForm();
        },
    });

    const callAddDirectorFromCIN = (cin) => {
        callGetCINDetails({ cin: cin })
            .then((response) => {
                if (response.code == 200) {
                    const allDirectors = response?.data.directors;
                    const directorArr = allDirectors.map((elem) => {
                        const fullName = elem.Name;
                        const nameParts = fullName.split(" ");

                        let firstName = nameParts[0];
                        let lastName = nameParts[nameParts.length - 1];

                        return {
                            firstName: firstName,
                            lastName: lastName,
                            din: elem["DIN/PAN"],
                            designation: elem.Designation,
                            dateOfAppointment: moment(
                                elem["Begin date"],
                                "DD/MM/YYYY"
                            ).format("YYYY-MM-DD"),
                            entityId: +entityId,
                            status: 3,
                            type: "director",
                        };
                    });


                    addAuthorisedPerson({ authorisedPerson: directorArr })
                        .then((response) => {
                            if (response.code == 201 && response.status) {
                                getAuthorisedPersonList({ entityId, type: "director" });
                            }
                        })
                        .catch((error) => {
                            console.log(error);
                        });
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const callAddShareholdingFromCIN = (cin) => {
        const postParams = {
            entity_id: +entityId,
            cin_llpin: cin,
        };
        callFinancialCINSubmit(postParams);
    };

    const dialogAction = (action, data) => {
        setShowCommonDialog({ isShow: false });
    };

    return (
        <div className="page-content">
            <Breadcrumbs title="Update Partner" />
            <div className="vertical-scroll">
                <Container fluid>
                    <Row>
                        <Col xs="12">
                            <Card>
                                <CardBody>
                                    {isEditEntityPermission && (
                                        <Form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                validation.handleSubmit();
                                                return false;
                                            }}
                                        >
                                            <Row>
                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">GSTIN</Label>
                                                    <Input
                                                        name="gstnIn"
                                                        type="text"
                                                        placeholder="Enter GSTIN"
                                                        onChange={(e) => {
                                                            validation.handleChange(e);
                                                            onGSTINChange(e.target.value);
                                                        }}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.gstnIn || ""}
                                                        invalid={
                                                            validation.touched.gstnIn &&
                                                            validation.errors.gstnIn
                                                        }
                                                    />
                                                    {validation.touched.gstnIn &&
                                                        validation.errors.gstnIn ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.gstnIn}
                                                        </FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">PAN*</Label>
                                                    <Input
                                                        name="pan"
                                                        type="text"
                                                        placeholder="Enter PAN"
                                                        onChange={(e) => {
                                                            validation.handleChange(e);
                                                            onPanChange(e.target.value);
                                                        }}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.pan || ""}
                                                        invalid={
                                                            validation.touched.pan && validation.errors.pan
                                                        }
                                                        disabled={true}
                                                    />
                                                    {validation.touched.pan && validation.errors.pan ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.pan}
                                                        </FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">Entity Name*</Label>
                                                    <Input
                                                        name="entityName"
                                                        type="text"
                                                        placeholder="Enter Entity Name"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.entityName || ""}
                                                        invalid={
                                                            validation.touched.entityName &&
                                                            validation.errors.entityName
                                                        }
                                                        disabled
                                                    />
                                                    {validation.touched.entityName &&
                                                        validation.errors.entityName ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.entityName}
                                                        </FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">
                                                        Date of Incorporation*
                                                    </Label>
                                                    <Input
                                                        name="dateOfIncorporation"
                                                        type="date"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.dateOfIncorporation || ""}
                                                        invalid={
                                                            validation.touched.dateOfIncorporation &&
                                                            validation.errors.dateOfIncorporation
                                                        }
                                                        max={currentDate}
                                                    />
                                                    {validation.touched.dateOfIncorporation &&
                                                        validation.errors.dateOfIncorporation ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.dateOfIncorporation}
                                                        </FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">
                                                        Entity Constitution*
                                                    </Label>
                                                    <Input
                                                        name="entityConstitution"
                                                        type="select"
                                                        placeholder="Enter Entity constitution"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.entityConstitution || ""}
                                                        invalid={
                                                            validation.touched.entityConstitution &&
                                                            validation.errors.entityConstitution
                                                        }
                                                    >
                                                        <option value="" disabled>
                                                            Select Entity Constitution
                                                        </option>
                                                        <option value="Private Limited Company">
                                                            Private Limited Company
                                                        </option>
                                                        <option value="Limited Liability Partnership">
                                                            Limited Liability Partnership
                                                        </option>
                                                        <option value="Proprietorship">Proprietorship</option>
                                                        <option value="Partnership">Partnership</option>
                                                        <option value="Public Limited Company">
                                                            Public Limited Company
                                                        </option>
                                                    </Input>
                                                    {validation.touched.entityConstitution &&
                                                        validation.errors.entityConstitution ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.entityConstitution}
                                                        </FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">
                                                        CIN
                                                        {validation.values.entityConstitution ==
                                                            "Private Limited Company" ||
                                                            validation.values.entityConstitution ==
                                                            "Public Limited Company"
                                                            ? "*"
                                                            : null}
                                                    </Label>
                                                    <Input
                                                        name="cin"
                                                        type="text"
                                                        placeholder="Enter CIN"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.cin || ""}
                                                        invalid={
                                                            validation.touched.cin && validation.errors.cin
                                                        }
                                                    />
                                                    {validation.touched.cin && validation.errors.cin ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.cin}
                                                        </FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">
                                                        Company Description
                                                    </Label>
                                                    <Input
                                                        name="companyDescription"
                                                        type="textarea"
                                                        placeholder="Company Description"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.companyDescription || ""}
                                                    />
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col className="mt-4">
                                                    <div className="text-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary me-3"
                                                            onClick={() => navigate(-1)}
                                                        >
                                                            Cancel
                                                        </button>

                                                        <button
                                                            type="submit"
                                                            className="btn btn-success save-customer"
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Form>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                        <PartnerAddressDetails />
                        <PartnerUserDetails />
                    </Row>

                </Container>
            </div>
            {
                showCommonDialog && (
                    <CommonDialog
                        isShow={showCommonDialog.isShow}
                        header={"Alert"}
                        message={showCommonDialog.message}
                        data={showCommonDialog.data}
                        postitiveBtn={"Save it"}
                        negativeBtn={"Close"}
                        onCloseFn={dialogAction}
                    />
                )
            }
            <ToastDialog
                message={toastInfo.message || ""}
                showToast={toastInfo.showToast}
                role={toastInfo.role}
                onCloseFn={() => setToastInfo({ showToast: false })}
            />
        </div>
    );
};

export default withRouter(UpdatePartner);

UpdatePartner.propTypes = {
    history: PropTypes.object,
};
